import { doc, getDoc, setDoc, updateDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { Settings, DEFAULT_SETTINGS, migrateSettings, validateSettings } from '../types/settings';
import { withErrorHandling, handleFirestoreError } from '../lib/errors';

export class SettingsService {
  private static instance: SettingsService;
  private cache = new Map<string, Settings>();

  private getSettingsDoc(userId: string) {
    return doc(db, 'Users', userId, 'settings', 'user');
  }

  /**
   * Get user settings with caching and validation
   * Firestore rule: allow get - if authed() && (userId == uid() || isAdmin())
   */
  async getSettings(userId: string): Promise<Settings> {
    return withErrorHandling(async () => {
      // Validate userId
      if (!userId || typeof userId !== 'string') {
        throw new Error('Invalid userId provided to getSettings');
      }
      
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to view settings');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Removed console.warn to prevent information leakage
        throw new Error('You can only view your own settings');
      }

      // Check cache first
      if (this.cache.has(userId)) {
        return this.cache.get(userId)!;
      }

      const settingsDoc = this.getSettingsDoc(userId);
      const docSnap = await getDoc(settingsDoc);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const settings = migrateSettings(data);
        const validatedSettings = validateSettings(settings);
        
        // Update cache
        this.cache.set(userId, validatedSettings);
        return validatedSettings;
      } else {
        // Create default settings
        const defaultSettings = {
          ...DEFAULT_SETTINGS,
          metadata: {
            ...DEFAULT_SETTINGS.metadata,
            version: 1,
            created: new Date(),
            lastUpdated: new Date(),
          }
        };
        
        await this.createSettings(userId, defaultSettings);
        this.cache.set(userId, defaultSettings);
        return defaultSettings;
      }
    }, {
      ctx: { feature: 'settings', action: 'getSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Create initial settings document
   * Firestore rule: allow create - if authed() && (userId == uid() || isAdmin())
   * Compliance: Called from getSettings which validates userId matches authenticated user
   */
  private async createSettings(userId: string, settings: Settings): Promise<void> {
    return withErrorHandling(async () => {
      const settingsDoc = this.getSettingsDoc(userId);
      const cleanedSettings = this.cleanUndefinedValues(settings);
      await setDoc(settingsDoc, cleanedSettings);
    }, {
      ctx: { feature: 'settings', action: 'createSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Update user settings
   * Firestore rule: allow update - if authed() && (userId == uid() || isAdmin())
   */
  async updateSettings(userId: string, updates: Partial<Settings>): Promise<void> {
    return withErrorHandling(async () => {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update settings');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Removed console.warn to prevent information leakage
        throw new Error('You can only update your own settings');
      }
      
      const settingsDoc = this.getSettingsDoc(userId);
      
      // Clean undefined values to prevent Firebase errors
      const cleanedUpdates = this.cleanUndefinedValues(updates);
      
      const updateData = {
        ...cleanedUpdates,
        metadata: {
          ...cleanedUpdates.metadata,
          version: cleanedUpdates.metadata?.version || 1,
          lastUpdated: new Date(),
        }
      };
      
      await updateDoc(settingsDoc, updateData);
      
      // Update cache
      if (this.cache.has(userId)) {
        const currentSettings = this.cache.get(userId)!;
        const updatedSettings = { ...currentSettings, ...cleanedUpdates };
        this.cache.set(userId, updatedSettings);
      }
    }, {
      ctx: { feature: 'settings', action: 'updateSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Batch update multiple settings fields
   * Firestore rule: allow update - if authed() && (userId == uid() || isAdmin())
   */
  async batchUpdateSettings(userId: string, updates: Partial<Settings>): Promise<void> {
    return withErrorHandling(async () => {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update settings');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Removed console.warn to prevent information leakage
        throw new Error('You can only update your own settings');
      }
      
      const batch = writeBatch(db);
      const settingsDoc = this.getSettingsDoc(userId);
      
      const updateData = {
        ...updates,
        metadata: {
          ...updates.metadata,
          version: updates.metadata?.version || 1,
          lastUpdated: new Date(),
        }
      };
      
      batch.update(settingsDoc, updateData);
      await batch.commit();
      
      // Update cache
      if (this.cache.has(userId)) {
        const currentSettings = this.cache.get(userId)!;
        const updatedSettings = { ...currentSettings, ...updateData };
        this.cache.set(userId, updatedSettings);
      }
    }, {
      ctx: { feature: 'settings', action: 'batchUpdateSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Reset settings to defaults
   * Firestore rule: allow update - if authed() && (userId == uid() || isAdmin())
   */
  async resetSettings(userId: string): Promise<void> {
    return withErrorHandling(async () => {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to reset settings');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Removed console.warn to prevent information leakage
        throw new Error('You can only reset your own settings');
      }
      
      const defaultSettings = {
        ...DEFAULT_SETTINGS,
        metadata: {
          ...DEFAULT_SETTINGS.metadata,
          version: 1,
          created: new Date(),
          lastUpdated: new Date(),
        }
      };
      
      await this.createSettings(userId, defaultSettings);
      this.cache.set(userId, defaultSettings);
    }, {
      ctx: { feature: 'settings', action: 'resetSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Subscribe to settings changes
   * Firestore rule: allow get - if authed() && (userId == uid() || isAdmin())
   */
  subscribeToSettings(userId: string, callback: (settings: Settings) => void): () => void {
    // Firestore rule compliance: verify userId matches authenticated user
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to subscribe to settings');
    }
    
    if (userId !== currentUser.uid) {
      // SECURITY: Removed console.warn to prevent information leakage
      throw new Error('You can only subscribe to your own settings');
    }
    
    const settingsDoc = this.getSettingsDoc(userId);
    
    const unsubscribe = onSnapshot(settingsDoc, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const settings = migrateSettings(data);
        const validatedSettings = validateSettings(settings);
        
        // Update cache
        this.cache.set(userId, validatedSettings);
        callback(validatedSettings);
      } else {
        // Document doesn't exist, create default settings
        this.resetSettings(userId).then(() => {
          callback(DEFAULT_SETTINGS);
        }).catch((error) => {
          handleFirestoreError(error, {
            feature: 'settings',
            action: 'subscribeToSettings',
            userId
          });
        });
      }
    }, (error) => {
      handleFirestoreError(error, {
        feature: 'settings',
        action: 'subscribeToSettings',
        userId
      });
    });

    return unsubscribe;
  }

  /**
   * Export settings as JSON
   */
  async exportSettings(userId: string): Promise<string> {
    return withErrorHandling(async () => {
      // SECURITY: IDOR Protection - getSettings will validate ownership
      const settings = await this.getSettings(userId);
      return JSON.stringify(settings, null, 2);
    }, {
      ctx: { feature: 'settings', action: 'exportSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Import settings from JSON
   */
  async importSettings(userId: string, jsonData: string): Promise<void> {
    return withErrorHandling(async () => {
      // SECURITY: Validate jsonData before parsing
      if (!jsonData || typeof jsonData !== 'string') {
        throw new Error('Invalid JSON data provided');
      }
      
      // SECURITY: Limit JSON data size to prevent DoS (max 1MB)
      if (jsonData.length > 1024 * 1024) {
        throw new Error('JSON data too large (max 1MB)');
      }
      
      // SECURITY: IDOR Protection - updateSettings will validate ownership
      let importedSettings: any;
      try {
        importedSettings = JSON.parse(jsonData);
      } catch (parseError) {
        throw new Error('Invalid JSON format');
      }
      
      // SECURITY: Validate parsed data is an object
      if (!importedSettings || typeof importedSettings !== 'object' || Array.isArray(importedSettings)) {
        throw new Error('Invalid settings format');
      }
      
      const validatedSettings = validateSettings(importedSettings);
      
      await this.updateSettings(userId, validatedSettings);
    }, {
      ctx: { feature: 'settings', action: 'importSettings', userId },
      domainHint: 'firestore'
    });
  }

  /**
   * Clear cache for a user
   */
  clearCache(userId?: string): void {
    if (userId) {
      this.cache.delete(userId);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clean undefined values from object to prevent Firebase errors
   */
  private cleanUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanUndefinedValues(item));
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.cleanUndefinedValues(value);
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  /**
   * Get singleton instance
   */
  static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }
}

// Export singleton instance
export const settingsService = SettingsService.getInstance();
